import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Check, ShieldCheck, LogOut } from "lucide-react";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "clearview_admin_token";

export default function AdminPricing() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingSlug, setSavingSlug] = useState(null);
  const [edits, setEdits] = useState({}); // { slug: { house_wash, driveway, gutter } }

  const loadPricing = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pricing`);
      setRows(res.data);
    } catch (e) {
      toast.error("Failed to load pricing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    sessionStorage.setItem(TOKEN_KEY, tokenInput.trim());
    setToken(tokenInput.trim());
    toast.success("Token saved for this session");
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setTokenInput("");
    toast.success("Logged out");
  };

  const setField = (slug, key, val) => {
    setEdits((e) => ({
      ...e,
      [slug]: {
        ...rows.find((r) => r.city_slug === slug),
        ...e[slug],
        [key]: val === "" ? "" : Number(val),
      },
    }));
  };

  const save = async (slug) => {
    const rowEdit = edits[slug];
    if (!rowEdit) return;
    const payload = {};
    ["house_wash", "driveway", "gutter"].forEach((k) => {
      if (rowEdit[k] !== "" && Number.isFinite(rowEdit[k])) {
        payload[k] = rowEdit[k];
      }
    });
    if (Object.keys(payload).length === 0) {
      toast.error("Nothing to save");
      return;
    }
    setSavingSlug(slug);
    try {
      const res = await axios.put(`${API}/pricing/${slug}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows((rs) => rs.map((r) => (r.city_slug === slug ? res.data : r)));
      setEdits((e) => {
        const { [slug]: _gone, ...rest } = e;
        return rest;
      });
      toast.success(`${res.data.city_name} updated`);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 401) {
        toast.error("Invalid admin token");
        handleLogout();
      } else {
        toast.error(err?.response?.data?.detail || "Save failed");
      }
    } finally {
      setSavingSlug(null);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center px-4">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 max-w-md w-full">
          <div className="h-12 w-12 rounded-xl bg-blue-600 text-white grid place-items-center mb-5">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
            Admin · Pricing
          </h1>
          <p className="text-sm text-slate-600 font-medium mt-2">
            Enter your admin token to edit per-city pricing.
          </p>
          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5 block">
                Admin token
              </Label>
              <Input
                data-testid="admin-token-input"
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="Paste token"
                className="bg-slate-50 border-slate-200 rounded-xl h-11"
                autoFocus
              />
            </div>
            <Button
              type="submit"
              data-testid="admin-token-submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold h-11 rounded-xl"
            >
              Continue
            </Button>
          </form>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-slate-600 hover:text-blue-600"
          >
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
        <Toaster richColors position="top-center" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-blue-600 uppercase tracking-wider"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to site
            </Link>
            <h1 className="font-display mt-2 text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
              City Pricing
            </h1>
            <p className="text-sm text-slate-600 font-medium mt-1">
              Edit any value and click Save. Changes go live instantly.
            </p>
          </div>
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-blue-600 uppercase tracking-wider"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>

        {loading && rows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500">
            <Loader2 className="h-6 w-6 mx-auto animate-spin" />
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-slate-200 bg-slate-50 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <div className="col-span-4">City</div>
              <div className="col-span-2 text-right">House wash</div>
              <div className="col-span-2 text-right">Driveway</div>
              <div className="col-span-2 text-right">Gutter</div>
              <div className="col-span-2 text-right">Save</div>
            </div>
            <div className="divide-y divide-slate-100">
              {rows.map((row) => {
                const e = edits[row.city_slug] || {};
                const dirty = e && Object.keys(e).some((k) =>
                  ["house_wash", "driveway", "gutter"].includes(k) && e[k] !== row[k] && e[k] !== ""
                );
                return (
                  <div
                    key={row.city_slug}
                    data-testid={`row-${row.city_slug}`}
                    className="grid grid-cols-12 gap-2 px-5 py-3 items-center"
                  >
                    <div className="col-span-4">
                      <div className="font-bold text-slate-900 text-sm">{row.city_name}</div>
                      <div className="text-[11px] text-slate-500 font-medium">/{row.city_slug}</div>
                    </div>
                    <PriceCell
                      value={e.house_wash !== undefined ? e.house_wash : row.house_wash}
                      onChange={(v) => setField(row.city_slug, "house_wash", v)}
                      testid={`input-house-${row.city_slug}`}
                    />
                    <PriceCell
                      value={e.driveway !== undefined ? e.driveway : row.driveway}
                      onChange={(v) => setField(row.city_slug, "driveway", v)}
                      testid={`input-driveway-${row.city_slug}`}
                    />
                    <PriceCell
                      value={e.gutter !== undefined ? e.gutter : row.gutter}
                      onChange={(v) => setField(row.city_slug, "gutter", v)}
                      testid={`input-gutter-${row.city_slug}`}
                    />
                    <div className="col-span-2 text-right">
                      <button
                        data-testid={`save-${row.city_slug}`}
                        onClick={() => save(row.city_slug)}
                        disabled={!dirty || savingSlug === row.city_slug}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold disabled:bg-slate-200 disabled:text-slate-400 hover:bg-blue-700 transition-colors"
                      >
                        {savingSlug === row.city_slug ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : dirty ? (
                          <><Save className="h-3.5 w-3.5" /> Save</>
                        ) : (
                          <><Check className="h-3.5 w-3.5" /> Saved</>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <Toaster richColors position="top-center" />
    </div>
  );
}

const PriceCell = ({ value, onChange, testid }) => (
  <div className="col-span-2">
    <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-lg px-2 h-9 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
      <span className="text-slate-400 text-sm font-bold">$</span>
      <input
        data-testid={testid}
        type="number"
        min="0"
        max="10000"
        value={value === "" ? "" : value}
        onChange={(ev) => onChange(ev.target.value)}
        className="w-full bg-transparent text-right text-sm font-bold text-slate-900 outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
    </div>
  </div>
);
